/**
 * VerifyStep Component
 *
 * Registration step 2: Verify phrase and complete registration.
 * Mobile-optimized with clear error handling.
 */

import { Alert, Button } from '@sisumail/ui';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type VerifyStatus = 'idle' | 'generating-keys' | 'registering';

interface VerifyStepProps {
    phrase: string;
    status: VerifyStatus;
    onVerify: () => void;
    onBack: () => void;
    error?: string;
}

export function VerifyStep({
    phrase,
    status,
    onVerify,
    onBack,
    error: externalError,
}: VerifyStepProps) {
    const [verifyInput, setVerifyInput] = useState('');
    const [internalError, setInternalError] = useState('');
    const error = externalError || internalError;
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Word-by-word visual feedback
    const phraseWords = phrase.toLowerCase().split(' ');
    const inputWords = verifyInput
        .trim()
        .toLowerCase()
        .split(/\s+/)
        .filter((w) => w);

    const _isComplete = inputWords.length >= 12;
    const isMatch = inputWords.join(' ') === phraseWords.join(' ');

    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    const handleVerify = () => {
        if (!isMatch) {
            setInternalError("The phrase doesn't match completely. Please double-check.");
            return;
        }
        onVerify();
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setVerifyInput(e.target.value);
        setInternalError('');
    };

    return (
        <motion.div
            key="verify"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="mx-auto w-full max-w-lg"
        >
            <div className="overflow-hidden rounded-3xl border border-neutral-800 bg-neutral-900 shadow-2xl">
                {/* Header */}
                <div className="border-neutral-800 border-b bg-neutral-950/50 p-8 text-center sm:p-10">
                    <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-500/20 bg-emerald-500/10 shadow-2xl shadow-emerald-500/5 transition-all">
                        <Check className="h-8 w-8 text-emerald-500" />
                    </div>
                    <h2 className="mb-3 font-bold text-3xl text-white tracking-tight">
                        Verify & Create
                    </h2>
                    <p className="px-4 font-medium text-neutral-500 text-sm leading-relaxed">
                        Please type your 12-word phrase to confirm you saved it correctly.
                    </p>
                </div>

                <div className="space-y-8 p-8 sm:p-10">
                    {/* Progress Bar */}
                    <div className="space-y-3">
                        <div className="flex justify-between font-bold text-[10px] uppercase tracking-[0.2em]">
                            <span className="text-neutral-500">Words Entered</span>
                            <span
                                className={
                                    inputWords.length >= 12
                                        ? 'text-emerald-500'
                                        : 'text-neutral-600'
                                }
                            >
                                {Math.min(inputWords.length, 12)} / 12 Words
                            </span>
                        </div>
                        <div className="h-2 overflow-hidden rounded-full border border-neutral-800 bg-neutral-950">
                            <motion.div
                                className="h-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.3)]"
                                initial={{ width: 0 }}
                                animate={{
                                    width: `${(Math.min(inputWords.length, 12) / 12) * 100}%`,
                                }}
                                transition={{ type: 'spring', stiffness: 100 }}
                            />
                        </div>
                    </div>

                    {/* Input */}
                    <div className="relative">
                        <textarea
                            ref={textareaRef}
                            value={verifyInput}
                            onChange={handleInputChange}
                            placeholder="Type your 12-word recovery phrase..."
                            disabled={status !== 'idle'}
                            className={`min-h-[160px] w-full resize-none rounded-2xl border bg-neutral-950 p-6 font-mono text-sm leading-relaxed outline-none transition-all ${
                                error
                                    ? 'border-red-500/50 shadow-[0_0_0_1px_rgba(239,68,68,0.2)]'
                                    : 'border-neutral-800 focus:border-emerald-500/50 focus:shadow-[0_0_0_1px_rgba(16,185,129,0.2)]'
                            } ${status !== 'idle' ? 'opacity-50' : 'text-neutral-200'}`}
                        />
                    </div>

                    {error && <Alert variant="error">{error}</Alert>}

                    {/* Actions */}
                    <div className="space-y-4">
                        <Button
                            onClick={handleVerify}
                            disabled={status !== 'idle' || !isMatch}
                            className="h-16 w-full font-bold text-lg uppercase tracking-widest"
                            variant="primary"
                        >
                            {status === 'generating-keys' || status === 'registering' ? (
                                <div className="flex items-center gap-3">
                                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-black border-t-transparent" />
                                    Creating Account...
                                </div>
                            ) : (
                                <>
                                    Verify & Create <ArrowRight className="ml-2 h-5 w-5" />
                                </>
                            )}
                        </Button>

                        <button
                            onClick={onBack}
                            disabled={status !== 'idle'}
                            className="flex w-full items-center justify-center gap-2 py-2 font-bold text-[10px] text-neutral-600 uppercase tracking-widest transition-colors hover:text-white"
                        >
                            <ArrowLeft size={12} /> Go back to view phrase
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
